"use client";

import { useCallback, useRef, useEffect } from "react";
import {
  type RecordingEngineReturn,
  QUALITY_CONSTRAINTS,
} from "./use-recording-engine";
import type { RecordingEngineOptions } from "../types";
import { useAppStore } from "@/stores";
import { getRecordingErrorMessage } from "@/features/recorder/utils/error-messages";
import { getVideoMimeType } from "../utils/mime-negotiation";
import { createOnDataAvailable } from "../utils/recorder-handlers";

export interface UseVideoRecordingOptions extends RecordingEngineOptions {
  videoQuality?: string;
  onDataAvailable?: (data: ArrayBuffer) => void;
}

export interface UseVideoRecordingReturn extends RecordingEngineReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}

export { getVideoMimeType };

/**
 * Build getUserMedia video constraints.
 * Uses { ideal } throughout — { exact } frameRate throws OverconstrainedError on virtual cameras.
 * On Android, facingMode exact is added to route to the correct physical camera
 * (UA detection is the only reliable mechanism; MediaTrackSupportedConstraints cannot
 * distinguish mobile from desktop).
 */
export function getVideoConstraints(
  selectedVideoDevice: string,
  videoQuality: string,
  cameraPosition: "user" | "environment" = "user",
): MediaTrackConstraints {
  const qc = QUALITY_CONSTRAINTS[videoQuality];
  const sizeConstraints = qc
    ? { width: { ideal: qc.width }, height: { ideal: qc.height } }
    : {};

  const frameRateConstraint = { frameRate: { ideal: 30 } };

  const facingModeConstraint = /android/i.test(navigator.userAgent)
    ? { facingMode: { exact: cameraPosition } }
    : {};

  if (selectedVideoDevice) {
    return {
      deviceId: { exact: selectedVideoDevice },
      ...sizeConstraints,
      ...frameRateConstraint,
      ...facingModeConstraint,
    };
  }

  return { ...sizeConstraints, ...frameRateConstraint, ...facingModeConstraint };
}

export function useVideoRecording(
  engine: RecordingEngineReturn,
  isActive: boolean,
  options?: UseVideoRecordingOptions,
): UseVideoRecordingReturn {
  const setRecordingStatus = useAppStore((s) => s.setRecordingStatus);
  const setIsMicActive = useAppStore((s) => s.setIsMicActive);

  // Survives async gap between attachTrackInactivityWatcher and recorder.onstop, and guards unmount-before-onstop.
  const inactivityDetachRef = useRef<(() => void) | null>(null);

  // Detach listeners if component unmounts before recorder.onstop fires.
  useEffect(() => () => { inactivityDetachRef.current?.(); }, []);

  const startRecording = useCallback(async () => {
    if (!isActive) return;

    const {
      streamRef,
      recorderRef,
      chunksRef,
      previewUrlRef,
      startTimer,
      stopTimer,
      completeRecording,
    } = engine;

    const videoQuality = options?.videoQuality ?? "720p";
    const onDataAvailable = options?.onDataAvailable;

    const videoConstraints = getVideoConstraints(
      engine.selectedVideoDevice,
      videoQuality,
      engine.cameraPosition,
    );

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          ...(engine.selectedAudioDevice ? { deviceId: { exact: engine.selectedAudioDevice } } : {}),
        },
      });
    } catch (err: unknown) {
      const mappedMessage = getRecordingErrorMessage(err, true, "video");
      if (mappedMessage !== null) {
        engine.setError(mappedMessage);
      }
      engine.discardRecording();
      return;
    }

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    chunksRef.current = [];
    streamRef.current = stream;
    engine.setActiveStream(stream);

    const mimeType = getVideoMimeType();

    const recorderOptions: MediaRecorderOptions = {
      mimeType,
      videoBitsPerSecond: 2500000,
      audioBitsPerSecond: 128000,
    };

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, recorderOptions);
    } catch {
      // MediaRecorder constructor throws if mimeType is rejected; fall back to browser default.
      recorder = new MediaRecorder(stream);
    }

    // Capture actual mimeType after construction — browser may choose a different codec
    // on the fallback path. recorder.mimeType is set at construction, not after start().
    const actualMimeType = recorder.mimeType || mimeType;

    recorderRef.current = recorder;

    recorder.ondataavailable = createOnDataAvailable(chunksRef, "video", onDataAvailable);

    recorder.onstop = () => {
      engine.stopMicHealthMonitor();

      inactivityDetachRef.current?.();
      inactivityDetachRef.current = null;

      stopTimer();

      // Prefer the recorder's runtime mimeType (fallback-path codec choice); completeRecording falls back to the first chunk's type.
      void completeRecording(recorder.mimeType);

      setRecordingStatus("stopped");
      setIsMicActive(false);

      // Stop all tracks so camera/mic indicator lights turn off.
      stream.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      engine.setActiveStream(null);
    };

    recorder.onerror = () => {
      stopTimer();
      setRecordingStatus("idle");
      setIsMicActive(false);
    };

    recorder.start(1000); // 1s timeslices
    engine.startTake("video", actualMimeType);

    engine.startMicHealthMonitor(stream, { videoTracks: true });

    inactivityDetachRef.current = engine.attachTrackInactivityWatcher(stream, () => {
      if (recorder.state !== "inactive") recorder.stop();
    });

    startTimer();

    setRecordingStatus("recording");
    setIsMicActive(true);
  }, [
    isActive,
    engine,
    options,
    setRecordingStatus,
    setIsMicActive,
  ]);

  const stopRecording = useCallback(() => {
    if (!isActive) return;

    const { recorderRef } = engine;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, [isActive, engine]);

  if (!isActive) {
    return {
      ...engine,
      startRecording: async () => {},
      stopRecording: () => {},
    };
  }

  return {
    ...engine,
    startRecording,
    stopRecording,
  };
}
